import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { updateOrder } from "../dependiencies/action";

const InfoRow = ({ icon, label, value, valueClass = "" }) => (
  <div className="d-flex align-items-start py-2 border-bottom" style={{ borderColor: "#eef2f6" }}>
    <div
      className="d-flex align-items-center justify-content-center me-3"
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: "#E8F2FB",
        color: "#1263AC",
        flexShrink: 0,
      }}
    >
      <i className={`fa ${icon}`} />
    </div>
    <div className="flex-grow-1">
      <div style={{ fontSize: 12, color: "#6B7B8C", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div className={`fw-semibold ${valueClass}`} style={{ color: "#1A2B47", wordBreak: "break-word" }}>
        {value ?? "—"}
      </div>
    </div>
  </div>
);

const statusBadge = (s) => {
  const v = `${s || ""}`.toLowerCase();
  if (v === "completed") return { bg: "#E7F6EC", color: "#16A34A" };
  if (v === "pending") return { bg: "#FFF5DA", color: "#DB9E00" };
  if (v === "canceled" || v === "cancelled") return { bg: "#FDECEF", color: "#E11D48" };
  return { bg: "#E8F2FB", color: "#1263AC" };
};

const paymentBadge = (s) => {
  const v = `${s || ""}`.toLowerCase();
  if (v === "paid") return { bg: "#E7F6EC", color: "#16A34A" };
  if (v === "pending") return { bg: "#FFF5DA", color: "#DB9E00" };
  return { bg: "#FDECEF", color: "#E11D48" };
};

function ServiceRequestDetailModal({ show, onClose, row, onUpdated, query }) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setStatus(row.status || "pending");
      setPaymentStatus(row.paymentStatus || "pending");
    }
  }, [row]);

  if (!row) return null;

  const dirty = status !== row.status || paymentStatus !== row.paymentStatus;

  const handleSave = async () => {
    const patch = {};
    if (status !== row.status) patch.status = status;
    if (paymentStatus !== row.paymentStatus) patch.paymentStatus = paymentStatus;
    if (Object.keys(patch).length === 0) return;
    try {
      setSaving(true);
      await dispatch(updateOrder(row._id, patch));
      onUpdated && onUpdated();
      onClose();
    } catch (e) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const sBadge = statusBadge(status);
  const pBadge = paymentBadge(paymentStatus);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static" scrollable>
      <Modal.Header
        closeButton
        style={{
          background: "linear-gradient(135deg, #1263AC 0%, #0E4D87 100%)",
          color: "#fff",
          border: "none",
        }}
      >
        <Modal.Title className="d-flex align-items-center" style={{ color: "#fff", fontWeight: 700 }}>
          <i className="fa fa-stethoscope me-2" />
          Service Request Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#F7FAFD", padding: 20 }}>
        {/* Header summary card */}
        <div
          className="p-3 mb-3"
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #E5EEF6",
            boxShadow: "0 2px 6px rgba(11,43,77,0.04)",
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <div>
              <div style={{ color: "#6B7B8C", fontSize: 12, letterSpacing: 0.4 }}>SERVICE</div>
              <h5 className="mb-1" style={{ color: "#1A2B47", fontWeight: 700 }}>
                {row.serviceName || "Service Booking"}
              </h5>
              <div style={{ color: "#6B7B8C", fontSize: 13 }}>
                <span className="fw-semibold" style={{ color: "#1263AC" }}>#{row.serviceNumber}</span>
                <span className="mx-2">•</span>
                Booked {moment(row.createdAt || row.requestDate).format("DD MMM YYYY, hh:mm A")}
              </div>
            </div>
            <div className="d-flex gap-2 mt-2 mt-md-0">
              <span
                className="px-3 py-1"
                style={{
                  background: sBadge.bg,
                  color: sBadge.color,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                <i className="fa fa-info-circle me-1" />
                {status}
              </span>
              <span
                className="px-3 py-1"
                style={{
                  background: pBadge.bg,
                  color: pBadge.color,
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                <i className="fa fa-credit-card me-1" />
                Payment: {paymentStatus}
              </span>
            </div>
          </div>
        </div>

        {/* Two-column info grid */}
        <div className="row">
          <div className="col-md-6">
            <div
              className="p-3 mb-3"
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E5EEF6",
              }}
            >
              <h6 className="mb-2" style={{ color: "#1263AC", fontWeight: 700 }}>
                <i className="fa fa-user me-2" />Patient Information
              </h6>
              <InfoRow icon="fa-user" label="Patient Name" value={row.customerName} />
              <InfoRow icon="fa-phone" label="Mobile" value={row.mobile} />
              <InfoRow icon="fa-male" label="Age" value={row.age ? `${row.age} years` : "—"} />
              <InfoRow icon="fa-venus-mars" label="Gender" value={row.gender} />
              <InfoRow icon="fa-stethoscope" label="Medical Problem" value={row.medicalProblem} />
            </div>
          </div>

          <div className="col-md-6">
            <div
              className="p-3 mb-3"
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E5EEF6",
              }}
            >
              <h6 className="mb-2" style={{ color: "#1263AC", fontWeight: 700 }}>
                <i className="fa fa-map-marker me-2" />Location & Schedule
              </h6>
              <InfoRow icon="fa-map-marker" label="State" value={row.state} />
              <InfoRow icon="fa-map" label="City" value={row.city} />
              <InfoRow icon="fa-location-arrow" label="Pincode" value={row.pin_code} />
              <InfoRow
                icon="fa-calendar"
                label="Request Date & Time"
                value={`${moment(row.requestDate).format("DD MMM YYYY")}${
                  row.requestTime ? ` • ${row.requestTime}` : ""
                }`}
              />
              <InfoRow
                icon="fa-money"
                label="Amount"
                value={`₹ ${Number(row.amount || 0).toLocaleString("en-IN")}`}
                valueClass=""
              />
            </div>
          </div>
        </div>

        {/* Update controls */}
        <div
          className="p-3"
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #E5EEF6",
          }}
        >
          <h6 className="mb-3" style={{ color: "#1263AC", fontWeight: 700 }}>
            <i className="fa fa-pencil me-2" />Update Booking
          </h6>
          <div className="row">
            <div className="form-group col-md-6 mb-2">
              <label className="form-label fw-semibold" style={{ color: "#1A2B47" }}>
                Service Status
              </label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="canceled">Cancelled</option>
              </select>
            </div>
            <div className="form-group col-md-6 mb-2">
              <label className="form-label fw-semibold" style={{ color: "#1A2B47" }}>
                Payment Status
              </label>
              <select
                className="form-select"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer style={{ background: "#F7FAFD", borderTop: "1px solid #E5EEF6" }}>
        <Button variant="light" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          style={{
            background: dirty ? "#1263AC" : "#9CB7CF",
            border: "none",
            fontWeight: 600,
          }}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" /> Saving…
            </>
          ) : (
            <>
              <i className="fa fa-save me-2" />Save Changes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ServiceRequestDetailModal;
